import { SUPPORTED_LOCALES } from '../consts/supported-locales'

/**
 * Identificativo mercato/paese della build (`REACT_APP_MARKET`).
 * Mercati supportati: `fm` (Micronesia), `tj` (Tagikistan). Default `tj`.
 */
export const MARKET_ID = process.env.REACT_APP_MARKET || 'tj'

/**
 * Se true, alla prima visita (senza `preferredLanguage` in localStorage) non si usa la lingua del browser:
 * si attiva subito la lingua del mercato. Utile per white-label con lingua fissa.
 */
export const FORCE_MARKET_LOCALE =
  process.env.REACT_APP_FORCE_MARKET_LOCALE === 'true' ||
  process.env.REACT_APP_FORCE_MARKET_LOCALE === '1'

/**
 * URL pubblico per file sotto `public/` (funziona con homepage non-root se `PUBLIC_URL` è impostato).
 * @param {string} path - Percorso assoluto tipo `/assets/...`
 * @returns {string}
 */
export function getPublicAssetUrl(path) {
  const normalized = path.startsWith('/') ? path : `/${path}`
  const base = process.env.PUBLIC_URL || ''
  return `${base}${normalized}`
}

/**
 * Logo FRED nel footer "Powered by" del login.
 * Usare sempre questo URL (mai `../assets/...`: in produzione con React Router il path relativo è sbagliato).
 */
export const LOGO_FRED_POWERED_BY_URL = getPublicAssetUrl(
  '/assets/logo_fred_senza_payoff_2.png',
)

/**
 * @typedef {Object} MarketBranding
 * @property {string} loginPrimary - Logo principale schermata login (chiaro)
 * @property {string} loginFlag - Bandiera / emblema sinistro login
 * @property {string} loginSeal - Sigillo / logo istituzionale destro login
 * @property {string} poweredByFred - Logo "Powered by" footer login
 * @property {string} toolbarWhite - Logo bianco toolbar desktop/mobile
 * @property {string} authLogoLight - Logo su sfondo chiaro (forgot/signup)
 * @property {string} authLogoDark - Logo su sfondo scuro (forgot/signup)
 */

/**
 * @typedef {Object} MarketDefinition
 * @property {string} buildDefaultLocale
 * @property {string} documentTitle
 * @property {MarketBranding} branding
 */

/** @type {Record<string, MarketDefinition>} */
const MARKET_REGISTRY = {
  /** Micronesia: francese di default; bandiera e sigillo istituzionali. */
  fm: {
    buildDefaultLocale: 'fr',
    documentTitle: 'ADaMS',
    branding: {
      loginPrimary: '/assets/brands/fm/login_primary.png',
      loginFlag: '/assets/Micronesia_flag.svg',
      loginSeal: '/assets/Seal_Micronesia.svg',
      poweredByFred: '/assets/logo_fred_senza_payoff_2.png',
      toolbarWhite: '/assets/brands/fm/toolbar_white.png',
      authLogoLight: '/assets/brands/fm/auth_logo_light.png',
      authLogoDark: '/assets/brands/fm/auth_logo_dark.png',
    },
  },
  /** Tagikistan: tagiko di default; `Flag_of_Tajikistan` e `GOV_tajikistan`. */
  tj: {
    buildDefaultLocale: 'tg',
    documentTitle: 'ADaMS',
    branding: {
      loginPrimary: '/assets/brands/tj/login_primary.png',
      loginFlag: '/assets/Flag_of_Tajikistan.png',
      loginSeal: '/assets/GOV_tajikistan.png',
      poweredByFred: '/assets/logo_fred_senza_payoff_2.png',
      toolbarWhite: '/assets/brands/tj/toolbar_white.png',
      authLogoLight: '/assets/brands/tj/auth_logo_light.png',
      authLogoDark: '/assets/brands/tj/auth_logo_dark.png',
    },
  },
}

const FALLBACK_MARKET = MARKET_REGISTRY.tj

/**
 * Lingua di default della build (fallback se browser non supportato / mercato forzato).
 * Deve essere in `SUPPORTED_LOCALES` e avere catalogo Lingui caricato in `index.js`.
 * @returns {string}
 */
export function getBuildDefaultLocale() {
  const fromEnv = process.env.REACT_APP_DEFAULT_LOCALE
  const raw =
    (fromEnv && SUPPORTED_LOCALES.includes(fromEnv)
      ? fromEnv
      : null) ??
    MARKET_REGISTRY[MARKET_ID]?.buildDefaultLocale ??
    FALLBACK_MARKET.buildDefaultLocale

  if (!SUPPORTED_LOCALES.includes(raw)) {
    return SUPPORTED_LOCALES.includes('en') ? 'en' : SUPPORTED_LOCALES[0]
  }
  return raw
}

/**
 * Definizione mercato risolta (con fallback se `REACT_APP_MARKET` non è in registry).
 * @returns {MarketDefinition}
 */
export function getMarketDefinition() {
  return MARKET_REGISTRY[MARKET_ID] ?? FALLBACK_MARKET
}

/**
 * Path pubblici risolti con `getPublicAssetUrl` per uso nei componenti.
 * @returns {Record<keyof MarketBranding, string>}
 */
export function getBrandingUrls() {
  const { branding } = getMarketDefinition()
  /** @type {Record<string, string>} */
  const out = {}
  for (const key of Object.keys(branding)) {
    out[key] = getPublicAssetUrl(branding[key])
  }
  return out
}

/**
 * Titolo documento per il mercato corrente.
 * @returns {string}
 */
export function getMarketDocumentTitle() {
  return getMarketDefinition().documentTitle
}
